import { ThemeEvents } from '@theme/events';

/**
 * Keeps the desktop product media grid showing the selected colour's image
 * first after a variant switch.
 *
 * snippets/product-media-gallery-content.liquid already sorts media so the
 * selected variant's image comes first, but that's server-side Liquid - it
 * only reflects whichever variant the page happened to load with. Clicking
 * a different colour swatch afterwards is a client-side update (no page
 * reload), so nothing re-ran that sort. This moves the matching <li> to the
 * front of the DOM instead - the CSS nth-of-type(n+5) rule in base.css that
 * caps the grid to 4 visible images then applies to whatever ends up in the
 * first 4 DOM positions automatically.
 *
 * Listens for both variant:update (the native theme's event, e.g.
 * sticky-add-to-cart.js, with the variant on event.detail.resource) AND
 * variant:change (dispatched by blocks/ai_gen_block_622562e.liquid, this
 * site's actual custom colour-swatch picker, with the variant on
 * event.detail.variant instead) - confirmed via the live site that this
 * product page uses the custom picker, not the native one, so relying on
 * variant:update alone silently did nothing when a swatch was clicked.
 */
function handleVariantEvent(event) {
  const variant = event.detail?.resource ?? event.detail?.variant;
  // variant:update's Liquid-rendered variant object has `featured_media.id`,
  // which lines up with this gallery's `data-media-id` (both come from
  // Liquid's own media model). variant:change's variant object instead
  // comes straight from this site's custom picker's
  // fetch('/products/{handle}.js') call - that endpoint's JSON has no
  // `featured_media` field at all, only `featured_image`, and - confirmed
  // directly against this store's live data - `featured_image.id` is a
  // *different* id space (the legacy Image resource id) than the media id
  // `data-media-id` uses, so matching on id for that event never found
  // anything. `featured_image.src` and each grid item's underlying <img>
  // src always share the same filename (only the CDN width/version query
  // params differ), regardless of which id space is in play, so that's
  // used as a fallback match for the variant:change case.
  const featuredMediaId = variant?.featured_media?.id;
  const featuredImageFile = variant?.featured_image?.src?.split('/').pop()?.split('?')[0];
  if (!featuredMediaId && !featuredImageFile) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const section = target.closest('.shopify-section');
  if (!section) return;

  section.querySelectorAll('.media-gallery__grid').forEach((grid) => {
    let match = featuredMediaId ? grid.querySelector(`[data-media-id="${featuredMediaId}"]`) : null;

    if (!match && featuredImageFile) {
      match = Array.from(grid.querySelectorAll('[data-media-id]')).find((item) => {
        const img = item.querySelector('img');
        const src = img && (img.currentSrc || img.src);
        return src && src.split('/').pop().split('?')[0] === featuredImageFile;
      });
    }

    if (match && grid.firstElementChild !== match) {
      grid.insertBefore(match, grid.firstElementChild);
    }
  });
}

document.addEventListener(ThemeEvents.variantUpdate, handleVariantEvent);
document.addEventListener('variant:change', handleVariantEvent);